import EventSummary from "../../types/EventSummary";
import EventDetails from "../../types/EventDetails";
import PhotoDetails from "../../types/PhotoDetails";
import ServicesHelper from "./ServicesHelper";

const EVENTS_FILE_URL = '/assets/config/events.json';

export const EventService = {
  async fetchAllEventYears(descending: boolean = false): Promise<number[]> {
    const data = await ServicesHelper.fetchAndValidateJSON(EVENTS_FILE_URL);

    var allYears = data.events.year.map((year: any) => year.id);

    if (descending) {
      allYears.sort((a: number, b: number) => {
        if (a < b) {
          return 1;
        }
        if (a > b) {
          return -1;
        }
        return 0;
      });
    }

    return allYears;
  },

  async fetchEvents(year: number = 0, descending: boolean = false): Promise<EventSummary[]> {
    const data = await ServicesHelper.fetchAndValidateJSON(EVENTS_FILE_URL);

    // Flatten and process the events data
    const allEvents = data.events.year.flatMap((yearData: any) => {
      if (!yearData.event) return []; // Skip if no events
      const eventsArray = Array.isArray(yearData.event) ? yearData.event : [yearData.event]; // Handle single event objects
      return eventsArray.map((event: any) => ({
        date: event.date || "",
        type: event.Type || "",
        shortTitle: event.shortTitle || "",
        longTitle: event.longTitle || "",
        location: event.location || "",
        eventURL: event.eventURL || "",
        eventThumb: event.eventThumb || "",
        pictureCount: event.pictureCount || "",
        organizer: event.organizer?.text || "",
        id: event.id || ""
      }));
    });
    // Filter by year if specified
    let filteredEvents = allEvents;
    if (year > 0) {
      filteredEvents = filteredEvents.filter(
        (event: EventSummary) => new Date(event.date).getFullYear() === year
      );
    }
    // Sort by date in descending order if specified
    if (descending) {
      filteredEvents = filteredEvents.sort(
        (a: EventSummary, b: EventSummary) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    }
    return filteredEvents;
  },

  async fetchEventDetails(id: string): Promise<EventDetails> {
    const picturesDataFile = `/assets/config/events/${id}.json`;
    const data = await ServicesHelper.fetchAndValidateJSON(picturesDataFile);
    // Map the JSON data into the EventPictures type
    return {
      id: data.id || "",
      date: data.date || "",
      shortTitle: data.shortTitle || "",
      longTitle: data.longTitle || "",
      pictureCount: data.pictureCount || "",
      title: data.title || "",
      eventhome: data.eventhome || "",
      shortname: data.shortname || "",
      organizer: {
        logo: data.organizer?.logo || "",
        href: data.organizer?.href || "",
        name: data.organizer?.name || "",
      },
      categories: data.categories.map((category: any) => ({
        id: category.id || "",
        title: category.title || "",
        type: category.type || "",
        tooltip: category.tooltip || "",
        pictures: category.pictures.map((picture: any) => ({
          id: picture.id || "",
          spot: picture.spot || "",
          height: picture.height || "",
          width: picture.width || "",
          filename: picture.filename || "",
          caption: picture.caption || "",
        })),
      })),
    };
  },

  async fetchPhotoDetails(eventId: string, photoId: string): Promise<PhotoDetails> {
    const picturesDataFile = `/assets/config/events/${eventId}.json`;
    const data = await ServicesHelper.fetchAndValidateJSON(picturesDataFile);
    
    // Find the category and picture with the matching photoId
    let foundCategory = null;
    let foundPicture = null;
    let previousPicture = null;
    let nextPicture = null;

    for (const category of data.categories) {
      const pictureIndex = category.pictures.findIndex((picture: any) => picture.id === photoId);
      if (pictureIndex !== -1) {
        foundCategory = category;
        foundPicture = category.pictures[pictureIndex];
        previousPicture = category.pictures[pictureIndex - 1] || null;
        nextPicture = category.pictures[pictureIndex + 1] || null;
        break; // Exit loop once we find the picture
      }
    }

    if (!foundPicture || !foundCategory) {
      throw new Error(`Photo with ID ${photoId} not found in event ${eventId}`);
    }
    
    // Map the JSON data into the EventPictures type
    return {
      id: data.id || "",
      date: data.date || "",
      year: new Date(data.date).getFullYear(),
      shortTitle: data.shortTitle || "",
      longTitle: data.longTitle || "",
      pictureCount: data.pictureCount || "",
      title: data.title || "",
      eventhome: data.eventhome || "",
      shortname: data.shortname || "",
      organizer: {
        logo: data.organizer?.logo || "",
        href: data.organizer?.href || "",
        name: data.organizer?.name || "",
      },
      category: {
        id: foundCategory.id || "",
        title: foundCategory.title || "",
        type: foundCategory.type || "",
        tooltip: foundCategory.tooltip || "",
        pictures: [],
      },
      previousPicture: previousPicture ? {
          id: previousPicture.id || "",
          spot: previousPicture.spot || "",
          height: previousPicture.height || "",
          width: previousPicture.width || "",
          filename: previousPicture.filename || "",
          caption: previousPicture.caption || "",
        } : null,
      selectedPicture: foundPicture ? {
          id: foundPicture.id || "",
          spot: foundPicture.spot || "",
          height: foundPicture.height || "",
          width: foundPicture.width || "",
          filename: foundPicture.filename || "",
          caption: foundPicture.caption || "",
        } : null,
      nextPicture: nextPicture ? {
          id: nextPicture.id || "",
          spot: nextPicture.spot || "",
          height: nextPicture.height || "",
          width: nextPicture.width || "",
          filename: nextPicture.filename || "",
          caption: nextPicture.caption || "",
        } : null
    };
  }



};